import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@hooks/useTheme";
import { useMutationData } from "@hooks/useMutationData";
import { CustomDatePicker } from "@components/Forms";
import OutlineButton from "../Buttons/OutlineButton";
import { useTranslation } from "react-i18next";
import ToastConstant from "../../configs/react-toastify/snackbar";
import { TOAST } from "../../constants";
import { PrimaryButton } from "@components/Buttons";

const RequestNoticeModal = ({
  verificationReportSeq,
  projectId,
  isOpen,
  onClose,
  "data-testid": dataTestId = "request-notice-modal",
}) => {
  const fieldContainerTestId = (name) => `${dataTestId}-field-${name}-container`;
  const fieldLabelTestId = (name) => `${dataTestId}-field-${name}-label`;
  const fieldInputTestId = (name) => `${dataTestId}-field-${name}-input`;
  const { t } = useTranslation();
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    noticeInfoWriteStatusCd: "REQUEST",
    writeCompletionExpectedDt: null,
  });

  useEffect(() => {
    if (isOpen) {
      setFormData({
        noticeInfoWriteStatusCd: "REQUEST",
        writeCompletionExpectedDt: null,
      });
    }
  }, [isOpen]);

  const createNoticeInfoMutation = useMutationData(
    "/notice_info",
    { method: "POST" },
    {
      onSuccess: (response) => {
        navigate(`/notice-information/${response.noticeInfoSeq}`);
      },
      onError: (error) => {
        console.error("Error creating notice info:", error);
      },
    },
  );

  const loading = createNoticeInfoMutation.isPending;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = () => {
    // Validate that checkbox is selected
    if (formData.noticeInfoWriteStatusCd !== "REQUEST") {
      ToastConstant(TOAST.error, t("common.please-select-write-type"));
      return;
    }

    // Validate date if provided
    if (formData.writeCompletionExpectedDt) {
      const selectedDate = new Date(formData.writeCompletionExpectedDt);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        ToastConstant(TOAST.error, t("common.completion-date-cannot-be-past"));
        return;
      }
    }

    if (!verificationReportSeq || !projectId) {
      console.error("Missing verificationReportSeq or projectId for notice info create");
      return;
    }

    createNoticeInfoMutation.mutate({
      verificationReportSeq,
      projectId,
      noticeInfoWriteStatusCd: formData.noticeInfoWriteStatusCd,
      writeCompletionExpectedDt: formData.writeCompletionExpectedDt,
    });
  };

  if (!isOpen) return null;

  return (
    <div
      data-testid={`${dataTestId}-overlay`}
      className={`fixed inset-0 z-50 flex items-center justify-start md:justify-center p-4 ${colors.bgBlurModal} backdrop-blur-[1px] overflow-x-auto`}
    >
      <div
        data-testid={dataTestId}
        className={`p-2 relative w-[512px] min-w-[512px] ${colors.bgModal} transform overflow-visible rounded-2xl shadow-xl transition-all duration-200`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`px-6 pt-4 ${colors.borderPrimary}`} data-testid={`${dataTestId}-header`}>
          <div className="flex items-center justify-between">
            <h2 className={`${colors.textPrimary} text-2xl font-bold uppercase`} data-testid={`${dataTestId}-title`}>
              {t("project.verification-report.request-notice")}
            </h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-8" data-testid={`${dataTestId}-content`}>
          {/* SCAN TYPE Section */}
          {/* <div className="mb-6">
            <h3 className={`${colors.textPrimary} text-lg font-semibold mb-2`}>
              {t("type")} <span className={colors.textRequired}> *</span>
            </h3>

            <div>
              <CustomCheckbox
                id="noticeInfoWriteStatusCd"
                checked={formData.noticeInfoWriteStatusCd === "REQUEST"}
                onChange={() => handleCheckboxChange('noticeInfoWriteStatusCd')}
                label={t("project.verification-report.notice-write")}
                disabled={loading}
                size="md"
              />
            </div>
          </div> */}

          {/* WRITE COMPLETION EXPECTED DATE Section */}
          <div className="mb-6" data-testid={fieldContainerTestId("write-completion-expected-date")}>
            <h3
              className={`${colors.textPrimary} text-lg font-semibold mb-2`}
              data-testid={fieldLabelTestId("write-completion-expected-date")}
            >
              {t("project.verification-report.write-completion-expected-date")}
            </h3>

            <div data-testid={fieldInputTestId("write-completion-expected-date")}>
              <CustomDatePicker
                selected={formData.writeCompletionExpectedDt ? new Date(formData.writeCompletionExpectedDt) : null}
                onChange={(date) => handleInputChange("writeCompletionExpectedDt", date)}
                placeholderText={t("project.select-date-placeholder")}
                dateFormat="yyyy-MM-dd"
                minDate={new Date()}
                size="sm"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3" data-testid={`${dataTestId}-footer`}>
            <OutlineButton
              data-testid={`${dataTestId}-cancel-button`}
              onClick={onClose}
              disabled={loading}
              size="md"
            >
              {t("common.cancel-button")}
            </OutlineButton>
            <PrimaryButton
              data-testid={`${dataTestId}-confirm-button`}
              onClick={handleSubmit}
              disabled={loading || formData.noticeInfoWriteStatusCd !== "REQUEST"}
            >
              {loading ? (
                <div className="flex items-center">
                  <div className={`mr-2 h-4 w-4 animate-spin rounded-full border-b-2 ${colors.toggleThumbBackground}`}></div>
                  {t("common.sending")}
                </div>
              ) : (
                t("common.send-notice-button")
              )}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestNoticeModal; 